/**
 * Frontend validation utilities for signup/login
 * These enforce the strict rules defined by the backend
 */

// ============ USERNAME VALIDATION ============
const USERNAME_REGEX = /^[a-zA-Z0-9]+$/;
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 30;

/**
 * Validates username according to backend rules
 * - Alphanumeric only (a-z, A-Z, 0-9)
 * - 3-30 characters
 * - Case-insensitive on backend (but frontend should accept any case)
 * @returns { valid: boolean, error?: string }
 */
export function validateUsername(username) {
    if (!username) {
        return { valid: false, error: 'Brugernavn er påkrævet' };
    }

    const trimmed = username.trim();

    if (trimmed.length < USERNAME_MIN_LENGTH) {
        return { valid: false, error: `Brugernavn skal være mindst ${USERNAME_MIN_LENGTH} tegn (har ${trimmed.length})` };
    }

    if (trimmed.length > USERNAME_MAX_LENGTH) {
        return { valid: false, error: `Brugernavn må være maksimalt ${USERNAME_MAX_LENGTH} tegn (har ${trimmed.length})` };
    }

    if (!USERNAME_REGEX.test(trimmed)) {
        return { valid: false, error: 'Brugernavn må kun indeholde bogstaver og tal (uden mellemrum, understreg, bindestreg osv.)' };
    }

    return { valid: true };
}

/**
 * Checks if username contains invalid characters
 * Used for input filtering to prevent invalid chars from being typed
 */
export function hasInvalidUsernameChars(char) {
    return !/[a-zA-Z0-9]/.test(char);
}

// ============ EMAIL VALIDATION ============
const EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const EMAIL_MAX_LENGTH = 255;

/**
 * Validates email according to backend rules
 * - Standard email format with domain extension
 * - Local part: a-z A-Z 0-9 . _ + -
 * - Domain part: a-z A-Z 0-9 . -
 * - Cannot start/end with dot in local part
 * - Cannot have consecutive dots
 * - Case-insensitive on backend
 * @returns { valid: boolean, error?: string }
 */
export function validateEmail(email) {
    if (!email) {
        return { valid: false, error: 'Email er påkrævet' };
    }

    const trimmed = email.trim();

    if (trimmed.length > EMAIL_MAX_LENGTH) {
        return { valid: false, error: `Email må være maksimalt ${EMAIL_MAX_LENGTH} tegn` };
    }

    // Basic regex check
    if (!EMAIL_REGEX.test(trimmed)) {
        return { valid: false, error: 'Ugyldigt email-format' };
    }

    // Extract local part (before @)
    const atIndex = trimmed.indexOf('@');
    const localPart = trimmed.substring(0, atIndex);

    // Cannot start with dot
    if (localPart.startsWith('.')) {
        return { valid: false, error: 'Email kan ikke starte med et punktum' };
    }

    // Cannot end with dot
    if (localPart.endsWith('.')) {
        return { valid: false, error: 'Email kan ikke slutte med et punktum før @-tegnet' };
    }

    // Cannot have consecutive dots
    if (localPart.includes('..')) {
        return { valid: false, error: 'Email kan ikke indeholde konsekutive punktummer (..)' };
    }

    return { valid: true };
}

/**
 * Checks if character is allowed in email local part (before @)
 */
export function isValidEmailLocalChar(char) {
    return /[a-zA-Z0-9._+-]/.test(char);
}

/**
 * Checks if character is allowed in email domain part (after @)
 */
export function isValidEmailDomainChar(char) {
    return /[a-zA-Z0-9.-]/.test(char);
}

/**
 * Filters email input to only allow valid characters
 * Handles @ symbol positioning
 */
export function filterEmailInput(value, oldValue) {
    if (!value) return value;

    // Check @ count
    const atCount = (value.match(/@/g) || []).length;
    if (atCount > 1) {
        // Remove extra @ symbols, keeping only the first one
        const parts = value.split('@');
        return parts[0] + '@' + parts.slice(1).join('').replace(/@/g, '');
    }

    // If no @ yet, only allow local part chars
    if (!value.includes('@')) {
        return value.split('').filter(char => isValidEmailLocalChar(char)).join('');
    }

    // If @ exists, validate both parts
    const [local, domain] = value.split('@');
    const filteredLocal = local.split('').filter(char => isValidEmailLocalChar(char)).join('');
    const filteredDomain = domain.split('').filter(char => isValidEmailDomainChar(char)).join('');

    return filteredLocal + '@' + filteredDomain;
}

// ============ PASSWORD VALIDATION ============
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 128;

/**
 * Validates password according to backend rules
 * - 8-128 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 number
 * - Case-sensitive
 * @returns { valid: boolean, error?: string, strength?: 'weak' | 'medium' | 'strong' }
 */
export function validatePassword(password) {
    if (!password) {
        return { valid: false, error: 'Kodeord er påkrævet', strength: 'weak' };
    }

    const hasMinLength = password.length >= PASSWORD_MIN_LENGTH;
    const hasMaxLength = password.length <= PASSWORD_MAX_LENGTH;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    if (!hasMinLength || !hasMaxLength) {
        return {
            valid: false,
            error: `Kodeord skal være mellem ${PASSWORD_MIN_LENGTH} og ${PASSWORD_MAX_LENGTH} tegn`,
            strength: 'weak'
        };
    }

    if (!hasUpperCase) {
        return { valid: false, error: 'Kodeord skal indeholde mindst ét stort bogstav', strength: 'weak' };
    }

    if (!hasLowerCase) {
        return { valid: false, error: 'Kodeord skal indeholde mindst ét lille bogstav', strength: 'weak' };
    }

    if (!hasNumber) {
        return { valid: false, error: 'Kodeord skal indeholde mindst ét tal', strength: 'weak' };
    }

    // Determine strength
    let strength = 'medium';
    if (password.length >= 12 && /[!@#$%^&*()_\-+=\[\]{};:'",.<>?/\\|`~]/.test(password)) {
        strength = 'strong';
    }

    return { valid: true, strength };
}

/**
 * Checks if two passwords match
 */
export function passwordsMatch(password, confirmPassword) {
    if (!password || !confirmPassword) {
        return { valid: false, error: 'Begge kodeord felter skal udfyldes' };
    }

    if (password !== confirmPassword) {
        return { valid: false, error: 'Kodeordene stemmer ikke overens' };
    }

    return { valid: true };
}

// ============ FIELD-SPECIFIC ERROR PARSING ============

/**
 * Parses error response from backend and extracts field-specific errors
 * Backend response format: { error: string, field?: string }
 * @returns { general?: string, username?: string, email?: string, password?: string, [key: string]: string }
 */
export function parseErrorResponse(errorData) {
    const errors = {};

    if (!errorData) return errors;

    // If it has a field property, it's field-specific
    if (errorData.field && errorData.error) {
        errors[errorData.field] = errorData.error;
    } else if (errorData.error) {
        errors.general = errorData.error;
    } else if (errorData.message) {
        errors.general = errorData.message;
    } else if (typeof errorData === 'string') {
        errors.general = errorData;
    }

    return errors;
}

/**
 * Checks if a user is likely registering with an existing account
 * (backend returns 400 with field error for duplicate username/email)
 */
export function isDuplicateError(errorData) {
    if (!errorData) return false;
    const errors = parseErrorResponse(errorData);
    return errors.username?.toLowerCase().includes('eksisterer') ||
        errors.email?.toLowerCase().includes('eksisterer') ||
        errors.general?.toLowerCase().includes('bruger') ||
        errors.general?.toLowerCase().includes('email');
}

// ============ CROSS-FIELD VALIDATION ============

/**
 * Validates entire signup form
 */
export function validateSignupForm(formData) {
    const errors = {};

    const usernameValidation = validateUsername(formData.username);
    if (!usernameValidation.valid) {
        errors.username = usernameValidation.error;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
        errors.email = emailValidation.error;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.valid) {
        errors.password = passwordValidation.error;
    }

    const matchValidation = passwordsMatch(formData.password, formData.confirmPassword);
    if (!matchValidation.valid) {
        errors.confirmPassword = matchValidation.error;
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}

/**
 * Validates entire login form
 */
export function validateLoginForm(formData) {
    const errors = {};

    if (!formData.emailOrUsername) {
        errors.emailOrUsername = 'Brugernavn eller email er påkrævet';
    }

    if (!formData.password) {
        errors.password = 'Kodeord er påkrævet';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}
